import { Controller } from "@hotwired/stimulus"

/**
 * Collapses tab-row items that don't fit, Monday.com style: tabs show
 * whole (popping in/out one at a time as the row resizes, never partially
 * clipped), the active item stays visible even when earlier tabs collapse,
 * and the reveal targets (the All Views menu) appear only while something
 * is collapsed.
 *
 * Fitting compares the container's scrollWidth to its clientWidth, so
 * wrappers inside it (the favorite/non-favorite zones and their padding)
 * need no width bookkeeping.
 */
export default class extends Controller {
  static targets = ["container", "item", "activeItem", "reveal"]

  connect() {
    // Observe the nav, not the container — the container's width changes
    // when we toggle tabs/reveal ourselves, which would re-trigger us
    this.observer = new ResizeObserver(() => this.sync())
    this.observer.observe(this.element)
    // Re-measure once webfonts land; they change tab widths without
    // resizing the nav
    document.fonts?.ready?.then(() => this.sync())
    this.sync()
  }

  disconnect() {
    this.observer?.disconnect()
    this.observer = null
  }

  sync() {
    const container = this.containerTarget
    const fits = () => container.scrollWidth <= container.clientWidth + 1

    this.itemTargets.forEach(item => item.classList.remove('d-none'))

    // Everything fits once the reveal button is gone?
    this.revealTargets.forEach(el => el.classList.add('d-none'))
    if (fits()) return

    this.revealTargets.forEach(el => el.classList.remove('d-none'))

    // Collapse whole tabs from the end (keeping the active one) until the
    // rest fit
    const active = this.hasActiveItemTarget ? this.activeItemTarget : null
    const hideable = this.itemTargets.filter(item => item !== active).reverse()

    for (const item of hideable) {
      if (fits()) break

      item.classList.add('d-none')
    }
  }
}
